import { Component, inject, OnInit } from '@angular/core';
import { MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent } from '@angular/material/legacy-autocomplete';
import { UntypedFormControl, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Owner } from 'src/app/shared/helpers/owner.decorator';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule, MatRippleModule } from '@angular/material/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { OrderByPipe } from 'src/app/shared/pipes/order-by.pipe';
import { EventBusService } from 'src/app/shared/services/event-bus.service';
import { LocumsMarginCalculatorEvents } from 'src/app/recruiting/locums-margin-calculator/store/enums/locums-margin-calculator-events';
import { CandidateSearch } from 'src/app/recruiting/locums-margin-calculator/models/candidate-search.models';
import { CandidateSearchSelectorsService } from '../../store/selectors/candidate-search.selectors';
import { WINDOW } from 'src/app/core/core.module';
import { jobDetailsLoaded } from 'src/app/recruiting/jobs/details/components/store/selectors/job-details.selectors';
import { Store } from '@ngrx/store';

@Owner('Wayfarers')
@UntilDestroy()
@Component({
    selector: 'nova-candidate-search',
    templateUrl: './candidate-search.component.html',
    styleUrls: ['./candidate-search.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        MatDialogModule,
        MatFormFieldModule,
        MatInputModule,
        MatAutocompleteModule,
        MatButtonModule,
        MatOptionModule,
        MatTooltipModule,
        MatIconModule,
        ReactiveFormsModule,
        OrderByPipe,
        FormsModule,
        MatRippleModule
    ]
})
export class CandidateSearchComponent implements OnInit {
    candidateSearchInput = new UntypedFormControl();

    private readonly eventBus = inject(EventBusService);
    private readonly candidateSearchSelectors = inject(CandidateSearchSelectorsService).candidateSearchSelectors;
    private readonly dialogRef = inject(MatDialogRef<CandidateSearchComponent>);
    private readonly window = inject(WINDOW);
    private readonly store = inject(Store);
    private readonly selectedJob = this.store.selectSignal(jobDetailsLoaded);

    selectedCandidate = this.candidateSearchSelectors.selectedCandidate;
    candidates = this.candidateSearchSelectors.candidates;
    isCandidateSelected = this.candidateSearchSelectors.isCandidateSelected;

    ngOnInit(): void {
        this.initializeComponent();
        this.setupEventListeners();
    }

    candidateSelected(event: MatAutocompleteSelectedEvent): void {
        const candidate = event.option.value;

        if (candidate) {
            this.missingNPIValidation(candidate);

            this.eventBus.emit({
                type: LocumsMarginCalculatorEvents.CANDIDATE_SELECTED,
                payload: candidate
            });
        }
    }

    resetCandidateSelection(): void {
        this.eventBus.emit({
            type: LocumsMarginCalculatorEvents.CANDIDATE_SELECTED_RESET
        });
        this.candidateSearchInput.setValue('', { emitEvent: true });
    }

    onContinue() {
        const jobId = this.selectedJob()?.job?.id;
        const selectedCandidateId = this.selectedCandidate().id;

        if (this.isCandidateSelected() && jobId != null && selectedCandidateId != null) {
            this.dialogRef.close();

            this.navigateToLocumsMarginCalculator(jobId, selectedCandidateId);
        }
    }

    candidateNameDisplay(candidate: CandidateSearch): string {
        if (candidate) {
            if (candidate.recruiterFullName) {
                return `${candidate.fullName} Recruiter: ${candidate.recruiterFullName}`;
            } else {
                return candidate.fullName;
            }
        }
        return '';
    }

    openCandidatePage(): void {
        this.dialogRef.close();
        const url = `//${this.window.location.host}/#/recruiting/candidates/${this.selectedCandidate().id}/new-profile/about`;
        this.window.open(url, `_blank`);
    }

    private initializeComponent(): void {
        if (this.isCandidateSelected()) {
            this.resetCandidateSelection();
        }
    }

    private setupEventListeners(): void {
        this.candidateSearchInput.valueChanges.pipe(untilDestroyed(this)).subscribe((query) => {
            if (typeof query === 'string') {
                this.eventBus.emit({
                    type: LocumsMarginCalculatorEvents.CANDIDATE_SEARCHED,
                    payload: query
                });
            }
        });
    }

    private navigateToLocumsMarginCalculator(jobId: number, selectedCandidateId: number): void {
        const url = `//${this.window.location.host}/#/recruiting/jobs/${jobId}/candidates/${selectedCandidateId}/locums-1099-margin`;
        this.window.open(url, `_blank`);
    }

    private missingNPIValidation(candidate: CandidateSearch) {
        if (candidate.npi) {
            this.candidateSearchInput.setErrors(null);
        } else {
            this.candidateSearchInput.setErrors({ missingNPI: true });
        }
    }
}
